import Breadcrumb from "@/components/Breadcrumb";
import CustomLinks from "@/components/CustomLinks";
import cloudflareLoader from "@/lib/cloudflare-images";
import ExpandableSummary from "@/src/components/ExpandableSummary";
import clsx from "clsx";
import Image from "next/legacy/image";
import { ReactNode, useEffect, useState } from "react";

interface SplitViewLayoutProps {
  images: { id: string; url: string; title: string }[];
  heading: string;
  introduction: string;
  children?: ReactNode;
  links?: {
    network: string;
    networkUsername: string;
    type: "socialLink" | "link" | "book" | "newsletter";
    linkUrl?: string;
    label?: string;
  }[];
  imageFirst?: boolean;
  addVerticalPaddingToChildren?: boolean;
  wrapperClassName?: string;
}

const SplitViewLayout = ({
  images,
  heading,
  introduction,
  children,
  links,
  addVerticalPaddingToChildren = true,
  wrapperClassName = "border-t lg:border-t-0 flex h-full grow flex-col space-y-6 md:space-y-12 md:py-6 lg:pt-12 lg:pb-12",
}: SplitViewLayoutProps) => {
  return (
    <div className={clsx("SplitViewLayout", wrapperClassName)}>
      <div
        className={clsx(
          "SplitViewLayout__content",
          "grid grid-cols-12 sm:gap-6 md:gap-12 md:px-6 lg:px-12"
        )}
      >
        <div
          className={clsx(
            "SplitViewLayout__desktopMedia",
            "col-span-12 hidden md:col-span-5 md:block xl:col-span-4"
          )}
        >
          <div className={clsx("flex h-full w-full px-6 md:px-0")}>
            <div
              className={clsx(
                "relative isolate aspect-square h-full w-full overflow-hidden rounded-2xl bg-zinc-100"
              )}
            >
              <_Images images={images} />
            </div>
          </div>
        </div>

        <div
          className={clsx(
            "SplitViewLayout__header",
            "col-span-12 md:col-span-7 xl:col-span-8"
          )}
        >
          <div className={clsx("h-full md:pb-12 lg:pb-12")}>
            <div
              className={clsx("SplitViewLayout__header", "h-full md:space-y-3")}
            >
              <h1
                className={clsx(
                  "SplitViewLayout__heading",
                  "heading-sm--spatial sm:heading-md--spatial max-w-5xl text-balance border-b py-6 max-md:px-6 md:border-none md:pb-0 md:pt-0"
                )}
              >
                {heading}
              </h1>
              <div
                className={clsx(
                  "SplitViewLayout__mobileMedia",
                  "block md:hidden"
                )}
              >
                <div
                  className={clsx(
                    "flex h-full w-full px-6 py-6 md:px-0 md:py-0"
                  )}
                >
                  <div
                    className={clsx(
                      "relative isolate aspect-square h-full w-full overflow-hidden rounded-2xl bg-zinc-100"
                    )}
                  >
                    <_Images images={images} />
                  </div>
                </div>
              </div>
              {!!introduction && (
                <div
                  className={clsx(
                    "SplitViewLayout__expandableSummary",
                    "mb:pb-0 px-6 pb-6 md:px-0 lg:px-0"
                  )}
                >
                  <ExpandableSummary summary={introduction} />
                </div>
              )}
              {links && links.length > 0 && (
                <div
                  className={clsx(
                    "SplitViewLayout__customLinks",
                    "max-md:px-6 lg:pt-3"
                  )}
                >
                  <CustomLinks links={links} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {!!children && (
        <div className={clsx("SplitViewLayout__content", "col-span-12")}>
          <div
            className={clsx({
              "px-6 lg:px-12": addVerticalPaddingToChildren,
            })}
          >
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const _Images = ({ images }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [shuffledImages, setShuffledImages] = useState([]);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true); // New state to track autoplay status

  const shuffleImages = (array) => {
    let currentIndex = array.length,
      randomIndex;
    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex],
        array[currentIndex],
      ];
    }
    return array;
  };

  useEffect(() => {
    const newShuffledImages = shuffleImages([...images]);
    setShuffledImages(newShuffledImages);
  }, [images]);

  useEffect(() => {
    if (isAutoPlaying) {
      const interval = setInterval(() => {
        setCurrentImageIndex(
          (prevIndex) => (prevIndex + 1) % shuffledImages.length
        );
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [shuffledImages, isAutoPlaying]); // Add isAutoPlaying to the dependency array

  const handleImageClick = () => {
    setCurrentImageIndex(
      (prevIndex) => (prevIndex + 1) % shuffledImages.length
    );
  };

  return (
    <div className={clsx("h-full w-full", "")} onClick={handleImageClick}>
      {shuffledImages.map((image, index) => {
        if (!image) return null;

        return (
          <div
            key={image.id}
            style={{ display: currentImageIndex === index ? "block" : "none" }}
            className={clsx("relative h-full w-full")}
          >
            {!!image.url && (
              <Image
                src={image.url}
                layout="fill"
                alt={image.title}
                objectFit="cover"
                className={clsx("")}
                priority
                sizes="(max-width: 768px) 100vw, 50vw"
                loader={cloudflareLoader}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SplitViewLayout;
